import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowLeft, Users, Plus, Loader, UserPlus, Mail,
  Clock, FileText, Trash2, Crown, Edit2, Eye, Activity
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  owner: { label: 'Owner', icon: Crown, color: '#FFD60A' },
  editor: { label: 'Editor', icon: Edit2, color: '#007AFF' },
  viewer: { label: 'Viewer', icon: Eye, color: '#5C5C5C' }
};

const TeamsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDesc, setTeamDesc] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');

  useEffect(() => {
    const tier = user?.subscription_tier || 'free';
    if (user?.role !== 'admin' && !['business', 'founding'].includes(tier)) {
      navigate('/pricing');
      return;
    }
    fetchTeams();
  }, [user]);

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/teams`, { withCredentials: true });
      setTeams(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetail = async (teamId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/teams/${teamId}`, { withCredentials: true });
      setSelectedTeam(data);
    } catch (err) {
      toast.error('Failed to load team');
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) { toast.error('Team name required'); return; }
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/teams`, { name: teamName, description: teamDesc }, { withCredentials: true });
      toast.success('Team created!');
      setShowCreateDialog(false);
      setTeamName('');
      setTeamDesc('');
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Email required'); return; }
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/teams/${selectedTeam._id}/invite`,
        { email: inviteEmail, role: inviteRole },
        { withCredentials: true }
      );
      toast.success(data.message);
      setShowInviteDialog(false);
      setInviteEmail('');
      fetchTeamDetail(selectedTeam._id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/teams/${selectedTeam._id}/members/${userId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchTeamDetail(selectedTeam._id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center">
        <Loader size={32} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8" data-testid="teams-page">
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        {!selectedTeam ? (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Teams
              </h1>
              <button
                onClick={() => setShowCreateDialog(true)}
                data-testid="create-team-btn"
                className="bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform flex items-center gap-2"
              >
                <Plus size={16} /> New Team
              </button>
            </div>

            {teams.length === 0 ? (
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-12 text-center">
                <Users size={48} className="mx-auto mb-4 text-[var(--text-muted)]" />
                <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No Teams Yet</h2>
                <p className="text-[var(--text-secondary)] mb-4">Create a team to start collaborating on ideas.</p>
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-[var(--secondary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)]"
                >
                  Create Your First Team
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {teams.map((team) => (
                  <button
                    key={team._id}
                    onClick={() => fetchTeamDetail(team._id)}
                    data-testid={`team-card-${team._id}`}
                    className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 text-left hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px] transition-all"
                  >
                    <h3 className="text-xl font-black mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{team.name}</h3>
                    {team.description && <p className="text-sm text-[var(--text-secondary)] mb-3">{team.description}</p>}
                    <div className="flex items-center gap-4 text-xs text-[var(--text-muted)]">
                      <span className="flex items-center gap-1"><Users size={12} /> {team.members?.length || 0} members</span>
                      <span className="flex items-center gap-1"><FileText size={12} /> {team.shared_ideas_count || 0} ideas</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => setSelectedTeam(null)}
              className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="text-sm">All Teams</span>
            </button>

            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{selectedTeam.name}</h1>
                {selectedTeam.description && <p className="text-sm text-[var(--text-secondary)]">{selectedTeam.description}</p>}
              </div>
              <button
                onClick={() => setShowInviteDialog(true)}
                data-testid="invite-member-btn"
                className="bg-[var(--secondary)] text-white font-bold px-4 py-2 border-2 border-[var(--border-default)] flex items-center gap-2 text-sm"
              >
                <UserPlus size={14} /> Invite
              </button>
            </div>

            {/* Members */}
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-6">
              <h2 className="font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Members ({selectedTeam.members?.length || 0})</h2>
              <div className="space-y-3">
                {selectedTeam.members?.map((m, i) => {
                  const rc = ROLE_CONFIG[m.role] || ROLE_CONFIG.viewer;
                  const RoleIcon = rc.icon;
                  return (
                    <div key={m.email || `member-${i}`} className="flex items-center justify-between p-3 border border-[var(--border-subtle)]">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center border border-[var(--border-default)]" style={{ backgroundColor: rc.color + '20' }}>
                          <RoleIcon size={14} style={{ color: rc.color }} />
                        </div>
                        <div>
                          <div className="text-sm font-bold">{m.name || m.email}</div>
                          <div className="text-xs text-[var(--text-muted)]">{m.email} - {rc.label}</div>
                        </div>
                      </div>
                      {m.role !== 'owner' && selectedTeam.owner_id === user?._id && (
                        <button
                          onClick={() => handleRemoveMember(m.user_id)}
                          className="p-1.5 text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white border border-[var(--border-default)] transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shared Ideas */}
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-6">
              <h2 className="font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Shared Ideas ({selectedTeam.shared_ideas?.length || 0})</h2>
              {selectedTeam.shared_ideas?.length > 0 ? (
                <div className="space-y-3">
                  {selectedTeam.shared_ideas.map((si, i) => (
                    <div key={si._id || `idea-${i}`} className="p-3 border border-[var(--border-subtle)]">
                      <div className="font-bold text-sm">{si.title}</div>
                      <div className="text-xs text-[var(--text-muted)] mt-1">
                        Shared by {si.sharer_name} - {si.category}
                      </div>
                      {si.tags?.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {si.tags.map((t, j) => (
                            <span key={j} className="text-[10px] px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border-subtle)]">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--text-muted)]">No ideas shared yet. Share ideas from the Dashboard using the share button.</p>
              )}
            </div>

            {/* Activity Feed */}
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
              <h2 className="font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <Activity size={16} /> Activity
              </h2>
              {selectedTeam.activity?.length > 0 ? (
                <div className="space-y-2">
                  {selectedTeam.activity.map((act, i) => (
                    <div key={`activity-${i}`} className="flex items-start gap-3 p-2 text-sm">
                      <Clock size={14} className="mt-0.5 text-[var(--text-muted)]" />
                      <div>
                        <span className="font-bold">{act.actor_name}</span>{' '}
                        <span className="text-[var(--text-secondary)]">{act.detail}</span>
                        <div className="text-xs text-[var(--text-muted)]">
                          {act.created_at ? new Date(act.created_at).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--text-muted)]">No activity yet.</p>
              )}
            </div>
          </>
        )}

        {/* Create Team Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
            <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Create Team</DialogTitle>
              <DialogDescription className="text-sm text-[var(--text-secondary)]">Start collaborating on ideas</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Team Name</label>
                <input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g. Product Team"
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                  data-testid="team-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Description (optional)</label>
                <input
                  value={teamDesc}
                  onChange={(e) => setTeamDesc(e.target.value)}
                  placeholder="What's this team about?"
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                />
              </div>
              <button
                onClick={handleCreateTeam}
                disabled={creating}
                data-testid="confirm-create-team-btn"
                className="w-full bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="max-w-md bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
            <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Invite Member</DialogTitle>
              <DialogDescription className="text-sm text-[var(--text-secondary)]">Invite by email - existing users join instantly</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Email</label>
                <input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  type="email"
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Role</label>
                <div className="flex gap-3">
                  {['editor', 'viewer'].map((r) => (
                    <button
                      key={r}
                      onClick={() => setInviteRole(r)}
                      className={`flex-1 p-3 border-2 text-sm font-bold text-center transition-colors ${
                        inviteRole === r ? 'bg-[var(--obsidian)] text-white border-[var(--border-default)]' : 'border-[var(--border-subtle)]'
                      }`}
                    >
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleInvite}
                disabled={inviting}
                data-testid="confirm-invite-btn"
                className="w-full bg-[var(--secondary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50"
              >
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeamsPage;
